import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiCall } from '../config/api';
import { ToastContext } from '../App';

interface StatisticsData {
  start_date: string;
  end_date: string;
  detailed_statistics: {
    general: {
      total_bookings: number;
      unique_students: number;
      unique_animals: number;
    };
    avg_per_day: {
      avg_bookings_per_day: number;
    };
    popular_times: Array<{
      start_time: string;
      usage_count: number;
    }>;
    weekday_stats: Array<{
      weekday: string;
      booking_count: number;
    }>;
    monthly_stats: Array<{
      month: string;
      booking_count: number;
    }>;
  };
  comparison_statistics: {
    days_comparison: number;
    variation_percentage: number;
    current_period: {
      start_date: string;
      end_date: string;
      stats: {
        general: {
          total_bookings: number;
          unique_students: number;
          unique_animals: number;
        };
      };
    };
    previous_period: {
      start_date: string;
      end_date: string;
      stats: {
        general: {
          total_bookings: number;
          unique_students: number;
          unique_animals: number;
        };
      };
    };
  };
  students_and_animals_with_bookings?: Array<{
    user_id: number;
    student_name: string;
    student_category: string;
    animal_id: number;
    animal_name: string;
    total_bookings: number;
  }>;
  bookings_by_student?: Array<{
    student_name: string;
    total_bookings: number;
  }>;
  detailed_stats?: {
    total_bookings: number;
    animals?: Array<{
      animal_name: string;
      total_bookings: number;
    }>;
    monthly_stats?: Array<{
      month: string;
      booking_count: number;
    }>;
  };
}

const StatisticsPage: React.FC = () => {
  const { user } = useAuth();
  const [statisticsData, setStatisticsData] = useState<StatisticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { showToast } = React.useContext(ToastContext);

  useEffect(() => {
    // Definir datas padrão (primeiro e último dia do mês corrente)
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1; // getMonth() retorna 0-11, precisamos 1-12
    
    // Criar strings de data no formato YYYY-MM-DD para evitar problemas de fuso horário
    const firstDayOfMonth = `${year}-${month.toString().padStart(2, '0')}-01`;
    const lastDayOfMonth = `${year}-${month.toString().padStart(2, '0')}-${new Date(year, month, 0).getDate().toString().padStart(2, '0')}`;
    
    console.log('Today:', today.toISOString());
    console.log('First day of month:', firstDayOfMonth);
    console.log('Last day of month:', lastDayOfMonth);
    
    setStartDate(firstDayOfMonth);
    setEndDate(lastDayOfMonth);
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      loadStatistics();
    }
  }, [startDate, endDate]);

  const formatDateForInput = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const formatDate = (dateString: string | number) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatTime = (timeString: string) => {
    return timeString;
  };

  const loadStatistics = async () => {
    if (!startDate || !endDate) {
      setError('Por favor, selecione as datas de início e fim.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await apiCall(`/bookings/statistics?start_date=${startDate}&end_date=${endDate}`, { method: 'GET' });
      
      if (data.error) {
        setError(data.message || data.error);
        setStatisticsData(null);
      } else {
        setStatisticsData(data);
        setError('');
      }
    } catch (err) {
      setError('Erro ao comunicar com o servidor');
      setStatisticsData(null);
    } finally {
      setLoading(false);
    }
  };

  const renderVariationIndicator = (variation: number) => {
    if (variation === 0) {
      return <small className="text-muted">Sem variação</small>;
    } else if (variation > 0) {
      return <small className="text-success"><i className="fas fa-arrow-up"></i> +{variation}%</small>;
    } else {
      return <small className="text-danger"><i className="fas fa-arrow-down"></i> {variation}%</small>;
    }
  };

  const renderComparisonDetails = (comparisonStats: any) => {
    if (!comparisonStats || !comparisonStats.current_period || !comparisonStats.previous_period) {
      return <div className="alert alert-info">Dados de comparação não disponíveis</div>;
    }
    
    const current = comparisonStats.current_period.stats?.general || {};
    const previous = comparisonStats.previous_period.stats?.general || {};
    
    return (
      <>
        <hr />
        <div className="row mt-3">
          <div className="col-md-6">
            <h6>Período Atual ({formatDate(comparisonStats.current_period.start_date)} - {formatDate(comparisonStats.current_period.end_date)})</h6>
            <ul className="list-unstyled">
              <li><i className="fas fa-check text-success me-2"></i>{current.total_bookings || 0} aulas</li>
              <li><i className="fas fa-users text-info me-2"></i>{current.unique_students || 0} alunos</li>
              <li><i className="fas fa-paw text-warning me-2"></i>{current.unique_animals || 0} animais</li>
            </ul>
          </div>
          <div className="col-md-6">
            <h6>Período Anterior ({formatDate(comparisonStats.previous_period.start_date)} - {formatDate(comparisonStats.previous_period.end_date)})</h6>
            <ul className="list-unstyled">
              <li><i className="fas fa-check text-success me-2"></i>{previous.total_bookings || 0} aulas</li>
              <li><i className="fas fa-users text-info me-2"></i>{previous.unique_students || 0} alunos</li>
              <li><i className="fas fa-paw text-warning me-2"></i>{previous.unique_animals || 0} animais</li>
            </ul>
          </div>
        </div>
      </>
    );
  };

  const renderDetailedStudentStats = (bookingsData: any[]) => {
    // Agrupar dados por aluno
    const studentsByCategory: any = {
      student: {},
      external: {}
    };

    bookingsData.forEach((booking: any) => {
      if (!booking.total_bookings || booking.total_bookings === 0) return;
      const category = booking.student_category || 'student';
      const studentId = booking.user_id;
      if (!studentsByCategory[category]) {
        studentsByCategory[category] = {};
      }
      if (!studentsByCategory[category][studentId]) {
        studentsByCategory[category][studentId] = {
          name: booking.student_name,
          category: category,
          total_bookings: 0,
          animals: {}
        };
      }
      studentsByCategory[category][studentId].total_bookings += booking.total_bookings;
      if (booking.total_bookings > 0) {
        studentsByCategory[category][studentId].animals[booking.animal_id] = {
          name: booking.animal_name,
          total_bookings: booking.total_bookings
        };
      }
    });

    const renderMiniCards = (students: any) => {
      return (
        <div className="row">
          {Object.values(students).map((student: any, index: number) => {
            if (student.total_bookings === 0) return null;
            return (
              <div key={index} className="col-lg-4 col-md-6 col-sm-12 mb-3">
                <div className="card mini-student-card h-100">
                  <div className="card-body p-2">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="fw-bold">{student.name}</span>
                      <span className={`badge bg-${student.category === 'external' ? 'info' : 'success'}`}>
                        {student.category === 'external' ? 'Externo' : 'Aluno'}
                      </span>
                    </div>
                    <div className="mb-1 text-success">
                      <i className="fas fa-check me-1"></i>{student.total_bookings} aulas
                    </div>
                    <div className="animal-breakdown ms-2">
                      {Object.values(student.animals).map((animal: any, animalIndex: number) => (
                        <div key={animalIndex} className="animal-stat-item p-1 mb-1">
                          <i className="fas fa-paw me-1"></i>{animal.name} - {animal.total_bookings} aulas
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    };

    return (
      <>
        {/* Alunos regulares */}
        {Object.keys(studentsByCategory.student).length > 0 && (
          <div className="card mb-4">
            <div className="card-header" style={{ backgroundColor: '#f3d9fa', color: '#4a3c0a' }}>
              <h5 className="mb-0"><i className="fas fa-user-graduate me-2"></i>Alunos Regulares</h5>
            </div>
            <div className="card-body p-2">
              {renderMiniCards(studentsByCategory.student)}
            </div>
          </div>
        )}

        {/* Alunos externos */}
        {Object.keys(studentsByCategory.external).length > 0 && (
          <div className="card mb-4">
            <div className="card-header" style={{ backgroundColor: '#f3d9fa', color: '#4a3c0a' }}>
              <h5 className="mb-0"><i className="fas fa-user-tie me-2"></i>Alunos Externos</h5>
            </div>
            <div className="card-body p-2">
              {renderMiniCards(studentsByCategory.external)}
            </div>
          </div>
        )}
      </>
    );
  };

  const renderTrainerSpecificStats = (data: StatisticsData) => {
    return (
      <>
        {/* Estatísticas por aluno */}
        {data.students_and_animals_with_bookings && renderDetailedStudentStats(data.students_and_animals_with_bookings)}

        {/* Top alunos */}
        {data.bookings_by_student && data.bookings_by_student.length > 0 && (
          <div className="card mb-4">
            <div className="card-header" style={{ backgroundColor: '#f3d9fa', color: '#4a3c0a' }}>
              <h5 className="mb-0"><i className="fas fa-trophy me-2"></i>Top Alunos</h5>
            </div>
            <div className="card-body">
              <div className="row flex-nowrap top-students-row">
                {data.bookings_by_student.slice(0, 6).map((student, index) => (
                  <div key={index} className="col-6 col-md-2 mb-3">
                    <div className="card border-0 bg-light top-student-card top-student-card-sm h-100">
                      <div className="card-body text-center p-2">
                        <div className="h5 mb-1">{index + 1}</div>
                        <div className="h6 student-name mb-1">{student.student_name}</div>
                        <div className="booking-count text-success mb-1">{student.total_bookings} aulas</div>
                        {index === 0 && <span className="badge bg-warning">1º Lugar</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderStudentSpecificStats = (data: StatisticsData) => {
    const detailedStats = data.detailed_stats || data.detailed_statistics?.general || {};

    return (
      <>
        {/* Total de aulas */}
        {detailedStats.total_bookings !== undefined && (
          <div className="card mb-4">
            <div className="card-header" style={{ backgroundColor: '#f3d9fa', color: '#4a3c0a' }}>
              <h5 className="mb-0"><i className="fas fa-calendar-check me-2"></i>Total de Aulas</h5>
            </div>
            <div className="card-body">
              <div className="display-4">{detailedStats.total_bookings}</div>
            </div>
          </div>
        )}

        {/* Evolução mensal */}
        {data.detailed_stats?.monthly_stats && data.detailed_stats.monthly_stats.length > 0 && (
          <div className="card mb-4">
            <div className="card-header" style={{ backgroundColor: '#f3d9fa', color: '#4a3c0a' }}>
              <h5 className="mb-0"><i className="fas fa-calendar-alt me-2"></i>Evolução Mensal (Últimos 12 Meses)</h5>
            </div>
            <div className="card-body">
              <div className="row">
                {data.detailed_stats.monthly_stats.map((month, index) => (
                  <div key={index} className="col-md-2 col-sm-4 col-6 mb-2">
                    <div className="text-center">
                      <div className="h6 mb-1">
                        {month.month ? new Date(month.month + '-01').toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }) : 'N/A'}
                      </div>
                      <div className="text-muted">{month.booking_count || 0} aulas</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Estatísticas por animal */}
        {data.detailed_stats?.animals && data.detailed_stats.animals.length > 0 && (
          <div className="card mb-4">
            <div className="card-header" style={{ backgroundColor: '#f3d9fa', color: '#4a3c0a' }}>
              <h5 className="mb-0"><i className="fas fa-paw me-2"></i>Animais com que treinou</h5>
            </div>
            <div className="card-body">
              <div className="row">
                {data.detailed_stats.animals.map((animal, index) => (
                  <div key={index} className="col-md-3 col-sm-4 col-6 mb-2">
                    <div className="text-center">
                      <div className="h6 mb-1">{animal.animal_name || 'N/A'}</div>
                      <div className="text-muted">{animal.total_bookings || 0} aulas</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="container py-4">
      <div className="row">
        <div className="col-12">
          <div className="card shadow">
            {/* Cabeçalho principal */}
            <div className="card-header" style={{ backgroundColor: '#a2449c', color: 'white' }}>
              <h5><i className="fas fa-chart-bar me-2"></i>Estatísticas</h5>
            </div>
            <div className="card-body">
              {/* Filtros sempre disponíveis */}
              <div className="row align-items-end mb-3">
                <div className="col-md-3 mb-2">
                  <label className="form-label">Data de Início:</label>
                  <input type="date" className="form-control" value={startDate} onChange={e => setStartDate(e.target.value)} />
                </div>
                <div className="col-md-3 mb-2">
                  <label className="form-label">Data de Fim:</label>
                  <input type="date" className="form-control" value={endDate} onChange={e => setEndDate(e.target.value)} />
                </div>
                <div className="col-md-3 mb-2 d-flex align-items-end">
                  <button className="btn" style={{ backgroundColor: '#a2449c', color: 'white', width: '100%' }} onClick={loadStatistics}>
                    <i className="fas fa-chart-bar me-2"></i>Carregar Estatísticas
                  </button>
                </div>
              </div>
              {/* Loading só nos dados */}
              {loading ? (
                <div className="d-flex justify-content-center my-5">
                  <div className="spinner-border" style={{ color: '#a2449c' }} role="status">
                    <span className="visually-hidden">A carregar...</span>
                  </div>
                </div>
              ) : (
                <>
                  {/* Removido bloco de erro direto, manter apenas Toast */}

                  {statisticsData && (
                    <div id="statisticsContent">
                      {/* Cabeçalho com período */}
                      <div className="card mb-4">
                        <div className="card-header" style={{ backgroundColor: '#f3d9fa', color: '#4a3c0a' }}>
                          <h5 className="mb-0"><i className="fas fa-chart-line me-2"></i>Estatísticas Detalhadas</h5>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6 mb-2 mt-2">
                              <strong>Período Analisado:</strong><br />
                              {formatDate(statisticsData.start_date)} a {formatDate(statisticsData.end_date)}
                            </div>
                            <div className="col-md-6 mb-2">
                              <strong>Comparação:</strong><br />
                              {statisticsData.comparison_statistics?.days_comparison || 0} dias vs período anterior homólogo
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Estatísticas gerais */}
                      {statisticsData.detailed_statistics?.general && (
                        <div className="card mb-4">
                          <div className="card-header" style={{ backgroundColor: '#f3d9fa', color: '#4a3c0a' }}>
                            <h5 className="mb-0"><i className="fas fa-chart-bar me-2"></i>Estatísticas Gerais</h5>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              <div className="col-md-3">
                                <div className="stat-item text-center">
                                  <div className="stat-number text-success">
                                    {statisticsData.detailed_statistics.general.total_bookings || 0}
                                  </div>
                                  <div className="stat-label">Total de Aulas</div>
                                  {renderVariationIndicator(statisticsData.comparison_statistics?.variation_percentage || 0)}
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="stat-item text-center">
                                  <div className="stat-number text-info">
                                    {statisticsData.detailed_statistics.general.unique_students || 0}
                                  </div>
                                  <div className="stat-label">Alunos Únicos</div>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="stat-item text-center">
                                  <div className="stat-number text-warning">
                                    {statisticsData.detailed_statistics.general.unique_animals || 0}
                                  </div>
                                  <div className="stat-label">Animais Únicos</div>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="stat-item text-center">
                                  <div className="stat-number text-primary">
                                    {statisticsData.detailed_statistics.avg_per_day?.avg_bookings_per_day 
                                      ? Number(statisticsData.detailed_statistics.avg_per_day.avg_bookings_per_day).toFixed(1) 
                                      : '0'}
                                  </div>
                                  <div className="stat-label">Média/Dia</div>
                                </div>
                              </div>
                            </div>
                            {renderComparisonDetails(statisticsData.comparison_statistics)}
                          </div>
                        </div>
                      )}

                      {/* Horários mais populares */}
                      {statisticsData.detailed_statistics?.popular_times && statisticsData.detailed_statistics.popular_times.length > 0 && (
                        <div className="card mb-4">
                          <div className="card-header" style={{ backgroundColor: '#f3d9fa', color: '#4a3c0a' }}>
                            <h5 className="mb-0"><i className="fas fa-clock me-2"></i>Horários Mais Populares</h5>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              {statisticsData.detailed_statistics.popular_times.map((time, index) => (
                                <div key={index} className="col-md-2 col-sm-4 col-6 mb-2">
                                  <div className="text-center">
                                    <div className="h5 mb-1">{formatTime(time.start_time)}</div>
                                    <div className="text-muted">{time.usage_count} aulas</div>
                                    {index === 0 && <span className="badge bg-warning">Mais Popular</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Dias da semana mais populares */}
                      {statisticsData.detailed_statistics?.weekday_stats && statisticsData.detailed_statistics.weekday_stats.length > 0 && (
                        <div className="card mb-4">
                          <div className="card-header" style={{ backgroundColor: '#f3d9fa', color: '#4a3c0a' }}>
                            <h5 className="mb-0"><i className="fas fa-calendar-alt me-2"></i>Dias da Semana Mais Populares</h5>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              {statisticsData.detailed_statistics.weekday_stats.map((day, index) => (
                                <div key={index} className="col-md-2 col-sm-4 col-6 mb-2">
                                  <div className="text-center">
                                    <div className="h6 mb-1">{day.weekday}</div>
                                    <div className="text-muted">{day.booking_count} aulas</div>
                                    {index === 0 && <span className="badge bg-success">Mais Popular</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Estatísticas específicas por tipo de utilizador */}
                      {user?.role === 'trainer' 
                        ? renderTrainerSpecificStats(statisticsData)
                        : renderStudentSpecificStats(statisticsData)
                      }
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsPage; 